import type { Logger } from '@interopio/gateway/logging/api';
import { createServer, type ListenOptions, type Server } from 'node:net';
import type { ManagementCommand } from '../../types/manage.d.ts';
import type { PipeServerOptions } from './types.d.ts';
import { unlink } from 'node:fs/promises';
import { sendCommand } from './client.ts';

async function isSocketStale(pipeName: string): Promise<{ stale: true } | { stale: false; pid: number }> {
    try {
        const response = await sendCommand({ path: pipeName }, { command: 'info' }, { timeout: 2000 }) as { pid?: number };
        // Got a response, socket is in use by another instance
        return { stale: false, pid: response?.pid ?? 0 };
    } catch {
        // Connection failed, socket is stale
        return { stale: true };
    }
}

async function cleanupSocketFile(logger: Logger, pipeName?: string) {
    if (process.platform === 'win32') {
        // On Windows, the pipe file is automatically removed when the server is closed.
        return;
    }
    if (pipeName) {
        try {
            await unlink(pipeName);
        }
        catch (e) {
            const code = (e as NodeJS.ErrnoException).code;
            if (code !== 'ENOENT') {
                logger.warn(`[${pipeName}] error while removing the pipe - ${pipeName} (${code})`, e);
            }
        }
    }
}

function createManagementServer({ logger, options, handler, onClose }: {
    options: ListenOptions,
    logger: Logger,
    handler: (msg: ManagementCommand) => Promise<unknown>,
    onClose?: () => void
}): Promise<Server> {
    const pipeName = options.path;

    if (logger.enabledFor('info')) {
        logger.info(`[${pipeName}] opening command server pipe, waiting for the data...`);
    }
    return new Promise<Server>((resolve, reject) => {
        try {
            const server: Server = createServer((stream) => {
                if (logger.enabledFor('debug')) {
                    logger.debug(`[${pipeName}] command pipe server got client, waiting for the data...`);
                }
                stream.on('close', () => {
                    if (logger.enabledFor('debug')) {
                        logger.debug(`[${pipeName}] client closed`);
                    }
                });
                stream.on('error', (error) => {
                    logger.error(`[${pipeName}] error on pipe stream`, error);
                });
                stream.on('data', async (data) => {
                    try {
                        const asString = data.toString('utf8');
                        const msg = JSON.parse(asString) as ManagementCommand;
                        if (logger.enabledFor('debug')) {
                            logger.debug(`[${pipeName}] received command ${msg.command}`);
                        }
                        handler(msg)
                            .then((result) => {
                                const response = JSON.stringify({result});
                                if (logger.enabledFor('debug')) {
                                    logger.debug(`[${pipeName}] command ${msg.command} processed successfully. sending : ${response}`);
                                }
                                stream.write(response, (e) => {
                                    if (e) {
                                        if (logger.enabledFor('debug')) {
                                            logger.debug(`[${pipeName}] error while sending response for command ${msg.command}`, e);
                                        }
                                    }
                                });

                            })
                            .catch((error: { message: string }) => {
                                const response = JSON.stringify({error: error.message});
                                logger.error(`[${pipeName}] processing command ${msg.command} failed, sending ${response}`, error);
                                stream.write(response, (e) => {
                                    if (e) {
                                        if (logger.enabledFor('debug')) {
                                            logger.debug(`[${pipeName}] error while sending error response for command ${msg.command}`, e);
                                        }
                                    }
                                });
                            })
                            .finally(() => {
                                // if (msg.command === 'shutdown') {
                                //     if (logger.enabledFor('info')) {
                                //         logger.info(`[${pipeName}] stopping management pipe server`);
                                //     }
                                //     server.close(onClose);
                                // }
                            });
                    }
                    catch (error) {
                        logger.error(`[${pipeName}] unable to parse the data`, error);
                    }
                });
            });
            server.on("listening", () => {
                if (logger.enabledFor('info')) {
                    logger.info(`[${pipeName}] ready for commands...`);
                }
                resolve(server);
            });
            server.on("error", (error) => {
                if ((error as NodeJS.ErrnoException).code === 'EADDRINUSE') {
                    if (pipeName) {
                        logger.info(`[${pipeName}] already in use, checking if socket is stale...`);
                        isSocketStale(pipeName).then((result) => {
                            if (result.stale) {
                                logger.info(`[${pipeName}] socket is stale, cleaning up and retrying...`);
                                cleanupSocketFile(logger, pipeName).then(() => {
                                    server.listen(options);
                                }).catch(error => {
                                    reject(error);
                                });
                            } else {
                                const msg = `[${pipeName}] another gateway-server instance is already running on this path (pid: ${result.pid})`;
                                logger.error(msg);
                                reject(new Error(msg));
                            }
                        }).catch(error => {
                            reject(error);
                        });
                        return;
                    }
                }
                logger.error(`[${pipeName}] error while opening the pipe - ${pipeName}`, error);
                reject(error);
            });
            server.on('close', () => onClose?.());
            server.listen(options);
        }
        catch (cause) {
            const msg = `error while opening the pipe - ${pipeName}`;
            logger.error(msg, cause);
            const error = new Error(msg) as Error & { cause?: unknown };
            error.cause = cause;
            reject(error);
        }
    });
}

export async function gatewayManagementServer({ options, logger, shutdown, info }: PipeServerOptions) {
    return await createManagementServer({
        options,
        logger,
        onClose: () => {
            if (logger.enabledFor('info')) {
                logger.info(`[${options.path}] command server closed`);
            }
        },
        handler: async (msg) => {
            if (msg.command === 'shutdown') {
                if (shutdown) {
                    // will not wait for the shutdown to complete, as the server will need to close the connection
                    // on which the shutdown command is received
                    void shutdown().catch(error => {
                        if (logger.enabledFor('error')) {
                            logger.error(`[${options.path}] error during shutdown`, error);
                        }
                    });
                    return 'shutdown initiated';
                }
                else {
                    return 'shutdown disabled';
                }
            }
            else if (msg.command === 'get-info' || msg.command === 'info') {
                return await info();
            }
        }
    });
}
